import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ReverseFeedback } from '../types';
import './FeedbackDetail.css';

const ReverseFeedbackDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState<ReverseFeedback | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadFeedback = useCallback(async (): Promise<void> => {
    if (!id) return;
    try {
      const response = await api.get<ReverseFeedback>(`/feedbacks/reverse/${id}`);
      setFeedback(response.data);
    } catch (err: any) {
      setError('Erro ao carregar feedback reverso');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadFeedback();
    }
  }, [id, loadFeedback]);

  const formatDate = useCallback(
    (value?: string): string =>
      value ? new Date(value).toLocaleDateString('pt-BR') : 'Não informado',
    [],
  );

  const formatAnswer = useCallback((value?: string): string => {
    if (!value) return 'Não informado';
    if (value === 'sim') return 'Sim';
    if (value === 'parcialmente') return 'Parcialmente';
    return 'Não';
  }, []);

  const summaryCards = useMemo(
    () =>
      feedback
        ? [
            {
              label: 'Score final',
              value: feedback.finalScore,
              description: 'Resultado consolidado do feedback reverso.',
            },
            {
              label: 'Ciclo',
              value: feedback.cycle?.name || 'Não definido',
              description: 'Período vinculado à devolutiva.',
            },
            {
              label: 'Anonimato',
              value: feedback.anonymous ? 'Sim' : 'Não',
              description: 'Forma escolhida para envio do retorno.',
            },
          ]
        : [],
    [feedback],
  );

  if (loading) {
    return (
      <div className="detail-state">
        <div className="detail-state-card">
          <span className="detail-badge">Carregando</span>
          <h1>Preparando feedback reverso</h1>
          <p>Organizando contexto, critérios e consolidado da devolutiva.</p>
        </div>
      </div>
    );
  }

  if (error || !feedback) {
    return (
      <div className="detail-state">
        <div className="detail-state-card">
          <span className="detail-badge">Indisponível</span>
          <h1>{error || 'Feedback reverso não encontrado'}</h1>
          <button onClick={() => navigate('/feedbacks')} className="detail-action-button is-secondary">
            Voltar para feedbacks
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="detail-page">
      <section className="detail-hero">
        <div className="detail-hero-copy">
          <span className="detail-badge">Feedback reverso</span>
          <h1>Detalhes da devolutiva</h1>
          <p>
            Consulte a percepção sobre clareza, suporte, desenvolvimento e sugestões práticas para
            fortalecer a liderança no próximo ciclo.
          </p>
        </div>

        <div className="detail-hero-actions">
          <button onClick={() => navigate('/feedbacks')} className="detail-action-button is-secondary">
            Voltar para feedbacks
          </button>
        </div>
      </section>

      <section className="detail-summary-grid">
        {summaryCards.map((card) => (
          <article className="detail-summary-card" key={card.label}>
            <span>{card.label}</span>
            <strong>{card.value}</strong>
            <p>{card.description}</p>
          </article>
        ))}
      </section>

      <section className="detail-surface">
        <div className="detail-section-header">
          <div>
            <span className="detail-section-kicker">Geral</span>
            <h2>Informações principais</h2>
          </div>
        </div>

        <div className="detail-info-grid">
          <div className="detail-info-card">
            <span>Desenvolvedor</span>
            <strong>{feedback.anonymous ? 'Anônimo' : feedback.developer?.name || 'Não informado'}</strong>
          </div>
          <div className="detail-info-card">
            <span>Gestor</span>
            <strong>{feedback.manager?.name || 'Não informado'}</strong>
          </div>
          <div className="detail-info-card">
            <span>Ciclo</span>
            <strong>{feedback.cycle?.name || 'Não definido'}</strong>
          </div>
          <div className="detail-info-card">
            <span>Data</span>
            <strong>{formatDate(feedback.createdAt)}</strong>
          </div>
        </div>
      </section>

      <section className="detail-content-grid">
        <article className="detail-surface">
          <div className="detail-section-header">
            <div>
              <span className="detail-section-kicker">Clareza</span>
              <h2>Clareza técnica</h2>
            </div>
          </div>

          <div className="detail-metrics-grid is-compact">
            <div className="detail-metric-card">
              <span>Score</span>
              <strong>{feedback.clarityScore}/5</strong>
            </div>
            <div className="detail-metric-card">
              <span>Resposta</span>
              <strong>{formatAnswer(feedback.clarityQuestion)}</strong>
            </div>
          </div>

          {feedback.clarityObservations && (
            <article className="detail-text-card">
              <h3>Observações</h3>
              <p>{feedback.clarityObservations}</p>
            </article>
          )}
        </article>

        <article className="detail-surface">
          <div className="detail-section-header">
            <div>
              <span className="detail-section-kicker">Suporte</span>
              <h2>Disponibilidade e suporte</h2>
            </div>
          </div>

          <div className="detail-metrics-grid is-compact">
            <div className="detail-metric-card">
              <span>Score</span>
              <strong>{feedback.availabilityScore}/5</strong>
            </div>
            <div className="detail-metric-card">
              <span>Resposta</span>
              <strong>{formatAnswer(feedback.availabilityQuestion)}</strong>
            </div>
          </div>

          {feedback.availabilityObservations && (
            <article className="detail-text-card">
              <h3>Observações</h3>
              <p>{feedback.availabilityObservations}</p>
            </article>
          )}
        </article>
      </section>

      <section className="detail-surface">
        <div className="detail-section-header">
          <div>
            <span className="detail-section-kicker">Desenvolvimento</span>
            <h2>Qualidade do feedback e evolução</h2>
          </div>
        </div>

        <div className="detail-metrics-grid is-compact">
          <div className="detail-metric-card">
            <span>Score</span>
            <strong>{feedback.feedbackQualityScore}/5</strong>
          </div>
          <div className="detail-metric-card">
            <span>Resposta</span>
            <strong>{formatAnswer(feedback.feedbackQualityQuestion)}</strong>
          </div>
        </div>

        {feedback.feedbackQualityObservations && (
          <article className="detail-text-card">
            <h3>Observações</h3>
            <p>{feedback.feedbackQualityObservations}</p>
          </article>
        )}
      </section>

      <section className="detail-surface">
        <div className="detail-section-header">
          <div>
            <span className="detail-section-kicker">Consolidado</span>
            <h2>Síntese da devolutiva</h2>
          </div>
        </div>

        <div className="detail-text-grid detail-text-grid-compact">
          <article className="detail-text-card is-highlight">
            <h3>O que o gestor faz bem</h3>
            <p>{feedback.whatManagerDoesWell}</p>
          </article>
          <article className="detail-text-card is-highlight">
            <h3>O que pode melhorar</h3>
            <p>{feedback.whatCanImprove}</p>
          </article>
          <article className="detail-text-card is-highlight">
            <h3>Sugestões práticas</h3>
            <p>{feedback.suggestions}</p>
          </article>
        </div>
      </section>
    </div>
  );
};

export default ReverseFeedbackDetail;
